/**
 * FullTextSearchUI
 *
 * Full-text search tool. Owns its own header (no generic page header).
 *
 * Right panel:
 *   – No selection  → Search Tips only
 *   – Row selected  → Document Details (title, category, link, date)
 *                     + "View full document" button → modal
 *
 * Modal: fetches artifact detail (including extracted text) and renders
 * it as an inline reading pane.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  Loader2,
  ExternalLink,
  FileText,
  FileSpreadsheet,
  FileCode2,
  Globe,
  X,
  AlertTriangle,
  BookOpen,
  Calendar,
  Building2,
  Tag,
  ChevronRight,
  Maximize2,
  Download,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useSearch,
  type SearchResult,
  type SearchResultArtifact,
} from '@/hooks/useSources';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { useWorkspaceDetail } from '@/hooks/useWorkspaceDetail';
import { apiFetch } from '@/lib/apiFetch';
import type { Tool } from '@/types/database';

interface Props {
  tool: Tool;
}

interface LocationState {
  preselectedWorkspaceId?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Friendly kind labels — never show raw slugs to users */
const KIND_LABELS: Record<string, string> = {
  html_snapshot: 'Web Page',
  pdf: 'PDF',
  docx: 'Word Document',
  doc: 'Word Document',
  xlsx: 'Spreadsheet',
  xls: 'Spreadsheet',
  csv: 'CSV',
  image: 'Image',
  audio: 'Audio',
  video: 'Video',
  other: 'Document',
};
function kindLabel(kind: string) {
  return KIND_LABELS[kind] ?? kind;
}

/**
 * Convert a URL filename into a readable title.
 * e.g. "2024-01_City_Council_Minutes.pdf" → "2024-01 City Council Minutes"
 */
function docTitle(a: SearchResultArtifact): string {
  const segments = a.url.split('/').filter(Boolean);
  const raw = segments[segments.length - 1] ?? '';
  let name: string;
  try {
    name = decodeURIComponent(raw.split('?')[0]);
  } catch {
    name = raw.split('?')[0];
  }
  // Strip extension
  name = name.replace(/\.[^.]+$/, '');
  // Replace separators with spaces and clean up
  name = name.replace(/[_\-+]+/g, ' ').trim();
  // Collapse multiple spaces
  name = name.replace(/\s{2,}/g, ' ');
  return name || raw;
}

function mimeIcon(mime: string | null | undefined, cls = 'w-4 h-4 shrink-0') {
  const m = mime ?? '';
  if (m.includes('pdf')) return <FileText className={`${cls} text-red-500`} />;
  if (m.includes('spreadsheet') || m.includes('excel'))
    return <FileSpreadsheet className={`${cls} text-green-600`} />;
  if (m.includes('html') || m.includes('xml'))
    return <FileCode2 className={`${cls} text-blue-500`} />;
  return <Globe className={`${cls} text-gray-400`} />;
}

function fmtBytes(n: number | null | undefined) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

function fmtDate(s: string | null | undefined) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function highlight(text: string, q: string) {
  const esc = (s: string) =>
    s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  if (!q.trim()) return esc(text);
  const safe = esc(text);
  const terms = q
    .trim()
    .split(/\s+/)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return safe.replace(
    new RegExp(`(${terms.join('|')})`, 'gi'),
    '<mark class="bg-yellow-200 rounded px-0.5">$1</mark>'
  );
}

// ── Full Document Modal ───────────────────────────────────────────────────────

interface ArtifactDetail {
  id: string;
  url: string;
  mime_type: string | null;
  byte_size: number | null;
  kind: string;
  department: string | null;
  meeting_type: string | null;
  meeting_date: string | null;
  first_seen_at: string;
  sources?: { name: string } | null;
  extractions?: Array<{
    id: string;
    method: string;
    status: string;
    page_count: number | null;
    text?: string;
  }>;
}

function DocumentModal({
  artifactId,
  searchQuery,
  onClose,
}: {
  artifactId: string;
  searchQuery: string;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<ArtifactDetail | null>(null);
  const [fullText, setFullText] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [signedUrlError, setSignedUrlError] = useState<string | null>(null);
  const [view, setView] = useState<'text' | 'preview'>('text');

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch(`/api/artifacts/${artifactId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as { data?: ArtifactDetail };
        if (cancelled) return;
        const d = json.data ?? null;
        setDetail(d);

        if (d?.extractions && d.extractions.length > 0) {
          const ext =
            d.extractions.find((e) => e.status === 'done') ?? d.extractions[0];
          // Try dedicated text endpoint first
          const textRes = await apiFetch(`/api/artifacts/${artifactId}/text`);
          if (!cancelled && textRes.ok) {
            const textJson = (await textRes.json()) as {
              data?: { text?: string };
            };
            setFullText(
              textJson.data?.text ?? (ext as { text?: string }).text ?? null
            );
          } else if (!cancelled) {
            setFullText((ext as { text?: string }).text ?? null);
          }
        }
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : 'Failed to load document');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [artifactId]);

  // Fetch a signed inline URL for PDF preview — must include ?download=false
  // or the browser forces an attachment download instead of rendering the
  // PDF inside the <iframe>.
  useEffect(() => {
    let cancelled = false;
    async function loadSignedUrl() {
      try {
        const previewRes = await apiFetch(
          `/api/artifacts/${artifactId}/download?download=false`
        );
        if (!previewRes.ok) throw new Error(`HTTP ${previewRes.status}`);
        const previewJson = (await previewRes.json()) as {
          data?: { url?: string };
        };
        if (!cancelled) setSignedUrl(previewJson.data?.url ?? null);
      } catch (e) {
        if (!cancelled)
          setSignedUrlError(
            e instanceof Error ? e.message : 'Failed to load preview'
          );
      }
    }
    void loadSignedUrl();
    return () => {
      cancelled = true;
    };
  }, [artifactId]);

  const isPdf = (detail?.kind ?? '').toLowerCase() === 'pdf';

  const title = detail
    ? docTitle(detail as unknown as SearchResultArtifact)
    : 'Loading…';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-4xl max-h-[92vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-white shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {mimeIcon(detail?.mime_type, 'w-5 h-5')}
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">{title}</p>
              {detail?.sources?.name && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {detail.sources.name}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-4">
            {detail && (
              <>
                {isPdf && signedUrl && (
                  <Button
                    size="sm"
                    variant={view === 'preview' ? 'default' : 'outline'}
                    onClick={() =>
                      setView(view === 'preview' ? 'text' : 'preview')
                    }
                    className="gap-1.5"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    {view === 'preview' ? 'Extracted Text' : 'Preview PDF'}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    window.open(detail!.url, '_blank', 'noopener,noreferrer')
                  }
                  className="gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download
                </Button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {signedUrlError && (
          <div className="px-6 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-700 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {signedUrlError}
          </div>
        )}
        {/* Modal body */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="flex-1 flex flex-col items-center justify-center py-20 text-center px-8">
            <AlertTriangle className="w-10 h-10 text-amber-400 mb-3" />
            <p className="font-semibold text-gray-700">
              Could not load document
            </p>
            <p className="text-sm text-gray-500 mt-1">{error}</p>
          </div>
        ) : view === 'preview' && isPdf && signedUrl ? (
          <iframe
            src={signedUrl}
            title={title}
            className="flex-1 w-full border-0"
          />
        ) : (
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Extracted text pane */}
            <div className="flex-1 overflow-y-auto px-7 py-6">
              <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-4">
                Extracted Text
              </p>
              {fullText ? (
                <div
                  className="prose prose-sm max-w-none text-gray-800 leading-relaxed whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{
                    __html: searchQuery
                      ? highlight(fullText, searchQuery)
                      : fullText,
                  }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <FileText className="w-12 h-12 mb-3 text-gray-200" />
                  <p className="font-medium text-gray-500">
                    No extracted text available
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    The document may not have been processed yet.
                  </p>
                  {isPdf && signedUrl && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setView('preview')}
                      className="mt-4 gap-1.5"
                    >
                      <FileText className="w-3.5 h-3.5" /> Preview PDF instead
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Metadata sidebar */}
            {detail && (
              <div className="w-56 shrink-0 border-l bg-gray-50 overflow-y-auto px-4 py-5 space-y-5">
                {(detail.department ||
                  detail.meeting_type ||
                  detail.meeting_date) && (
                  <section>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                      Category
                    </p>
                    <div className="space-y-1 text-xs">
                      {detail.department && (
                        <p className="font-semibold text-gray-800">
                          {detail.department}
                        </p>
                      )}
                      {detail.meeting_type && (
                        <p className="text-gray-600">{detail.meeting_type}</p>
                      )}
                      {detail.meeting_date && (
                        <p className="text-gray-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />{' '}
                          {fmtDate(detail.meeting_date)}
                        </p>
                      )}
                    </div>
                  </section>
                )}
                <section>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                    File
                  </p>
                  <dl className="space-y-1.5 text-xs">
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500">Kind</dt>
                      <dd className="font-medium text-gray-800">
                        {kindLabel(detail.kind)}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500">Size</dt>
                      <dd className="text-gray-700">
                        {fmtBytes(detail.byte_size)}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500">First seen</dt>
                      <dd className="text-gray-700">
                        {fmtDate(detail.first_seen_at)}
                      </dd>
                    </div>
                  </dl>
                </section>
                <section>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                    Processing
                  </p>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex items-center gap-1.5">
                      {detail.extractions && detail.extractions.length > 0 ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                      )}
                      <span className="text-gray-700">Text extracted</span>
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Search Tips (right panel, no result selected) ─────────────────────────────

const TIPS = [
  'Type a word or phrase — matches are highlighted in yellow.',
  'Click any result row to see full document details on the right.',
  'Use ↑ ↓ arrow keys to move between results, and Enter to open the document.',
  'Click "View full document" to read the full extracted text inline.',
  'Searches scan all ingested government records in this workspace.',
];

function SearchTips() {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
      <div className="flex items-center gap-1.5 mb-4">
        <BookOpen className="h-3.5 w-3.5 text-gray-400 shrink-0" />
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
          Search Tips
        </p>
      </div>
      <ul className="space-y-3">
        {TIPS.map((tip, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gray-400 shrink-0" />
            <span className="text-sm text-gray-800 leading-snug">{tip}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Document Detail panel (result selected) ───────────────────────────────────

function DetailPanel({
  result,
  onViewDocument,
  onAnalyze,
  onClose,
}: {
  result: SearchResult;
  onViewDocument: () => void;
  onAnalyze: () => void;
  onClose: () => void;
}) {
  const a = result.artifact;
  const hasArchivedCopy = Boolean(a?.storage_path);

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm flex flex-col overflow-hidden">
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {mimeIcon(a?.mime_type)}
          <span className="text-sm font-semibold text-gray-900">
            Document Details
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700 p-0.5 rounded"
          aria-label="Close details"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* View full document — top CTA */}
      <div className="px-4 pt-4 shrink-0 space-y-2">
        <Button
          className="w-full gap-2 bg-gray-900 hover:bg-gray-800 text-white"
          onClick={onViewDocument}
        >
          <Maximize2 className="w-4 h-4" />
          View full document
        </Button>
        <Button
          className="w-full gap-2 bg-blue-600 border-blue-600 hover:bg-blue-700 hover:border-blue-700 text-white"
          onClick={onAnalyze}
        >
          <ChevronRight className="w-4 h-4" />
          Analyze in Document Q&amp;A
        </Button>
      </div>

      <div className="px-4 py-4 space-y-5 overflow-y-auto flex-1">
        {/* Document title */}
        {a && (
          <section>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">
              Document
            </p>
            <p className="text-sm font-semibold text-gray-900 leading-snug break-words">
              {docTitle(a)}
            </p>
          </section>
        )}

        {/* Category */}
        {a && (a.department || a.meeting_type || a.meeting_date) && (
          <section>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">
              Category
            </p>
            <div className="space-y-1.5">
              {a.department && (
                <div className="flex items-center gap-1.5 text-sm">
                  <Building2 className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                  <span className="font-semibold text-gray-900">
                    {a.department}
                  </span>
                </div>
              )}
              {a.meeting_type && (
                <div className="flex items-center gap-1.5 text-sm">
                  <Tag className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                  <span className="text-gray-800">{a.meeting_type}</span>
                </div>
              )}
              {a.meeting_date && (
                <div className="flex items-center gap-1.5 text-sm">
                  <Calendar className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                  <span className="text-gray-800">
                    {fmtDate(a.meeting_date)}
                  </span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Date added */}
        {a?.first_seen_at && (
          <section>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">
              Date Added
            </p>
            <p className="text-sm text-gray-900 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-gray-500 shrink-0" />
              {fmtDate(a.first_seen_at)}
            </p>
          </section>
        )}

        {/* Source & link */}
        {a && (
          <section>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">
              Source
            </p>
            {a.sources?.name && (
              <p className="text-sm font-semibold text-gray-900 mb-1.5">
                {a.sources.name}
              </p>
            )}
            {hasArchivedCopy ? (
              <button
                type="button"
                onClick={onViewDocument}
                className="text-xs text-blue-700 hover:underline break-all leading-relaxed flex items-start gap-1 text-left"
              >
                <ExternalLink className="w-3 h-3 mt-0.5 shrink-0" />
                {a.url} <span className="text-gray-400">(archived)</span>
              </button>
            ) : (
              <a
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-700 hover:underline break-all leading-relaxed flex items-start gap-1"
              >
                <ExternalLink className="w-3 h-3 mt-0.5 shrink-0" />
                {a.url}
              </a>
            )}
          </section>
        )}

        {/* File metadata */}
        {a && (
          <section>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">
              File
            </p>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between gap-2">
                <dt className="text-gray-600">Type</dt>
                <dd className="font-medium text-gray-900">
                  {kindLabel(a.kind)}
                </dd>
              </div>
              {a.byte_size && (
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-600">Size</dt>
                  <dd className="text-gray-800">{fmtBytes(a.byte_size)}</dd>
                </div>
              )}
            </dl>
          </section>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export const FullTextSearchUI: React.FC<Props> = ({ tool }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const state = (location.state ?? {}) as LocationState;
  const { currentWorkspace } = useWorkspaces();

  // Workspace resolution: URL param → location state → global selector
  const urlWorkspaceId = searchParams.get('workspace') ?? undefined;
  const [localWorkspaceId, setLocalWorkspaceId] = useState<string | undefined>(
    urlWorkspaceId ??
      state.preselectedWorkspaceId ??
      currentWorkspace?.id ??
      undefined
  );

  useEffect(() => {
    if (urlWorkspaceId && urlWorkspaceId !== localWorkspaceId)
      setLocalWorkspaceId(urlWorkspaceId); // eslint-disable-line react-hooks/set-state-in-effect
  }, [urlWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentWorkspace?.id && currentWorkspace.id !== localWorkspaceId)
      setLocalWorkspaceId(currentWorkspace.id); // eslint-disable-line react-hooks/set-state-in-effect
  }, [currentWorkspace?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (localWorkspaceId && localWorkspaceId !== searchParams.get('workspace'))
      setSearchParams({ workspace: localWorkspaceId }, { replace: true });
  }, [localWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const workspaceId = localWorkspaceId;
  const { data: wsDetail } = useWorkspaceDetail(workspaceId);
  const workspaceName =
    wsDetail?.workspace?.name ?? currentWorkspace?.name ?? undefined;

  // Search state
  const { results, loading, query, search } = useSearch(workspaceId);
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Selection + modal
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [modalArtifactId, setModalArtifactId] = useState<string | null>(null);
  const selectedResult =
    selectedIdx !== null ? (results[selectedIdx] ?? null) : null;

  const openModal = useCallback(
    (idx: number) => {
      const r = results[idx];
      if (r?.artifact?.id) setModalArtifactId(r.artifact.id);
    },
    [results]
  );

  // Keyboard navigation
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      // If modal is open, Escape is handled by DocumentModal — don't interfere
      if (modalArtifactId) return;
      if (!results.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx((i) => Math.min((i ?? -1) + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx((i) => Math.max((i ?? 1) - 1, 0));
      } else if (e.key === 'Enter' && selectedIdx !== null) {
        openModal(selectedIdx);
      } else if (e.key === 'Escape') {
        setSelectedIdx(null);
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [results.length, selectedIdx, modalArtifactId, openModal]);

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSelectedIdx(null);
      void search(input.trim());
    },
    [input, search]
  );

  return (
    <>
      {modalArtifactId && (
        <DocumentModal
          artifactId={modalArtifactId}
          searchQuery={query}
          onClose={() => setModalArtifactId(null)}
        />
      )}

      <div className="flex flex-col gap-4">
        {/* Page header */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <button
              type="button"
              onClick={() => navigate('/tools')}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              Tools
            </button>
            {workspaceId && (
              <>
                <span className="text-gray-400 text-sm">/</span>
                <button
                  type="button"
                  onClick={() => navigate(`/workspaces/${workspaceId}`)}
                  className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  {workspaceName ?? 'Workspace'}
                </button>
              </>
            )}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-baseline gap-2 flex-wrap">
            Full Text Search
            {workspaceName && (
              <span className="text-xl font-semibold text-gray-900">
                ({workspaceName})
              </span>
            )}
          </h1>
          <p className="mt-1 text-gray-600">
            {tool.description ??
              'Search for words or phrases found within workspace documents.'}
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4 items-start">
          {/* Left: search + results */}
          <div className="space-y-3">
            {/* Search bar */}
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <Input
                  ref={inputRef}
                  placeholder="Search for a word or phrase…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  className="pl-9 pr-9 bg-white text-gray-900 caret-gray-900 placeholder:text-gray-400"
                  autoFocus
                />
                {input && (
                  <button
                    type="button"
                    onClick={() => {
                      setInput('');
                      setSelectedIdx(null);
                      void search('');
                      inputRef.current?.focus();
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-gray-900 hover:bg-gray-800 text-white shrink-0"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                <span className="ml-1.5 hidden sm:inline">Search</span>
              </Button>
            </form>

            {/* Result count */}
            {query && results.length > 0 && (
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>
                  <span className="font-medium text-gray-900">
                    {results.length}
                  </span>{' '}
                  passage{results.length !== 1 ? 's' : ''} for{' '}
                  <span className="font-medium text-gray-900">
                    &ldquo;{query}&rdquo;
                  </span>
                </span>
                <span className="hidden sm:inline text-xs text-gray-400">
                  ↑↓ navigate · Enter to open
                </span>
              </div>
            )}

            {/* Results list */}
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : query && results.length === 0 ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-10 text-center">
                <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-2" />
                <p className="font-semibold text-amber-900">
                  No results for &ldquo;{query}&rdquo;
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Try a different word or phrase.
                </p>
              </div>
            ) : results.length > 0 ? (
              <div className="space-y-2">
                {results.map((r, idx) => {
                  const a = r.artifact;
                  const isSelected = selectedIdx === idx;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      className={[
                        'w-full text-left rounded-xl border px-4 py-3.5 transition-all',
                        'hover:border-gray-400 hover:shadow-sm',
                        isSelected
                          ? 'border-gray-900 bg-gray-50 ring-2 ring-gray-200 shadow-sm'
                          : 'border-gray-200 bg-white',
                      ].join(' ')}
                      onClick={() => setSelectedIdx(isSelected ? null : idx)}
                      onDoubleClick={() => {
                        setSelectedIdx(idx);
                        openModal(idx);
                      }}
                    >
                      <div className="flex items-start gap-3">
                        {mimeIcon(a?.mime_type)}
                        <div className="flex-1 min-w-0">
                          {/* Document title — full readable name */}
                          <p className="text-sm font-semibold text-gray-900 leading-snug mb-1.5">
                            {a ? docTitle(a) : '—'}
                          </p>
                          {/* Category pills */}
                          {a &&
                            (a.department ||
                              a.meeting_type ||
                              a.meeting_date) && (
                              <div className="flex flex-wrap gap-1 mb-2">
                                {a.department && (
                                  <span className="text-[11px] text-gray-700 bg-gray-100 rounded px-1.5 py-0.5 font-semibold">
                                    {a.department}
                                  </span>
                                )}
                                {a.meeting_type && (
                                  <span className="text-[11px] text-gray-700 bg-gray-100 rounded px-1.5 py-0.5">
                                    {a.meeting_type}
                                  </span>
                                )}
                                {a.meeting_date && (
                                  <span className="text-[11px] text-gray-600 bg-gray-100 rounded px-1.5 py-0.5">
                                    {fmtDate(a.meeting_date)}
                                  </span>
                                )}
                              </div>
                            )}
                          {/* Matched text snippet */}
                          <p
                            className="text-sm text-gray-800 leading-relaxed line-clamp-2"
                            dangerouslySetInnerHTML={{
                              __html: highlight(r.text, query),
                            }}
                          />
                        </div>
                        <ChevronRight
                          className={`w-4 h-4 mt-0.5 shrink-0 transition-colors ${
                            isSelected ? 'text-gray-900' : 'text-gray-300'
                          }`}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-10 text-center">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-base font-semibold text-gray-700 mb-1">
                  Search workspace documents
                </p>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  Type a word or phrase to find exact matches across all
                  extracted text from government records.
                </p>
              </div>
            )}
          </div>

          {/* Right panel (desktop) */}
          <div className="hidden xl:block">
            {selectedResult ? (
              <DetailPanel
                result={selectedResult}
                onViewDocument={() => openModal(selectedIdx!)}
                onAnalyze={() => {
                  const a = selectedResult.artifact;
                  if (!a) return;
                  navigate('/tools/document-qa', {
                    state: { preselectedArtifact: a },
                  });
                }}
                onClose={() => setSelectedIdx(null)}
              />
            ) : (
              <SearchTips />
            )}
          </div>
        </div>

        {/* Right panel (mobile) */}
        {selectedResult && (
          <div className="xl:hidden">
            <DetailPanel
              result={selectedResult}
              onViewDocument={() => openModal(selectedIdx!)}
              onAnalyze={() => {
                const a = selectedResult.artifact;
                if (!a) return;
                navigate('/tools/document-qa', {
                  state: { preselectedArtifact: a },
                });
              }}
              onClose={() => setSelectedIdx(null)}
            />
          </div>
        )}
      </div>
    </>
  );
};
